import React, { useContext } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from "axios";
import {
  CreateAPIParams,
  SendRequestParams,
  TokenizedAPIParams,
  WebServerContextType,
} from "../types";
import { activeOrgHeader } from "../utils/activeOrg";
import { isOrgRefusal, reportOrgRefusal } from "../utils/orgRefusal";
import { getAccessToken } from "./auth0-instance";

const baseUrlSec = process.env.REACT_APP_ENV !== "dev" ? "https://" : "http://";
const baseURL = process.env.REACT_APP_BACKEND_ENDPOINT || "";
// const ACAO =
//   process.env.REACT_APP_ENV !== "dev"
//     ? process.env.REACT_APP_ENV === "staging"
//       ? "https://mos-id-backend-staging.vectech.io"
//       : "https://mos-id-backend.vectech.io"
//     : "*";

const apiConfig = {
  mainAPI: {
    baseURL: baseUrlSec + baseURL,
  },
};

const WebServerContext = React.createContext<WebServerContextType | undefined>(
  undefined,
);

const WebServerProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { isLoading, user } = useAuth0();

  // Shared with the RTK Query layer, so the opening burst collapses onto one
  // silent auth for both. A token we could not get now rejects - with an error
  // isAuthError recognises when the session has gone - rather than resolving to
  // undefined and sending `Bearer undefined` for the API to answer 401 to.
  const getToken = () => getAccessToken();

  // Scout has two live request layers - this one and axiosBaseQuery in
  // auth0-instance.ts, which backs the RTK Query slices. Both need the header;
  // neither covers the other.
  const createApi = (params: CreateAPIParams) =>
    axios.create({
      baseURL: params.baseURL,
      headers: {
        Authorization: `Bearer ${params.token}`,
        "Content-Type": "application/json",
        // "Access-Control-Allow-Origin": ACAO,
        ...activeOrgHeader(),
      },
    });

  const createTokenizedAPI = async (params: TokenizedAPIParams) => {
    const token = await getToken();
    return createApi({
      baseURL:
        params?.devPort && process.env.REACT_APP_ENV === "dev"
          ? String(apiConfig.mainAPI.baseURL).slice(0, -4) + params.devPort
          : apiConfig.mainAPI.baseURL,
      token,
    });
  };

  const sendRequest =
    (method: string) =>
    async <T, D>(
      sendParams: SendRequestParams<D>,
    ): Promise<AxiosResponse<T, D>> => {
      const { devPort, ...rest } = sendParams;
      const resp: AxiosRequestConfig = {
        ...rest,
        method,
      };
      const api = await createTokenizedAPI({ devPort });
      try {
        return await api.request(resp);
      } catch (err) {
        const { response } = err as AxiosError;
        if (isOrgRefusal(response?.status, response?.data)) reportOrgRefusal();
        throw err;
      }
    };

  return (
    <WebServerContext.Provider
      value={{ sendRequest, getToken, isLoading, user }}
    >
      {children}
    </WebServerContext.Provider>
  );
};

const useWebServer = () => {
  const context = useContext(WebServerContext);
  if (!context) {
    throw new Error("UseWebServer must be used within a WebServerProvider");
  }
  return context;
};

export default WebServerProvider;
export { useWebServer };
